import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";
import { formatDisplayNumber } from "../utils/numberFormat";

const cardStyle = {
  backgroundColor: "#111",
  border: "1px solid rgba(255, 204, 0, 0.18)",
  borderRadius: "16px",
  padding: "20px",
  color: "#fff",
  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.18)",
};

const tableHeadStyle = {
  padding: "12px",
  textAlign: "right",
  fontWeight: "bold",
};

const tableCellStyle = {
  padding: "14px 12px",
  textAlign: "right",
  borderTop: "1px solid rgba(255,255,255,0.08)",
  color: "#fff",
};

const inputStyle = {
  width: "100%",
  backgroundColor: "#1b1b1b",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "10px",
  padding: "12px 14px",
  outline: "none",
};

const actionButtonStyle = {
  border: "none",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: "bold",
};

const toggleButtonStyle = (active) => ({
  border: "none",
  borderRadius: "999px",
  padding: "8px 14px",
  cursor: "pointer",
  fontWeight: "bold",
  backgroundColor: active ? "#ffcc00" : "#2a2a2a",
  color: active ? "#000" : "#fff",
  transition: "all 0.2s ease",
});

function SettingToggle({ title, description, enabled, onToggle }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        padding: "14px 0",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div>
        <strong style={{ display: "block", marginBottom: "6px" }}>{title}</strong>
        <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>{description}</span>
      </div>
      <button type="button" style={toggleButtonStyle(enabled)} onClick={onToggle}>
        {enabled ? "فعال" : "غیرفعال"}
      </button>
    </div>
  );
}

function TransferManagement() {
  const [settings, setSettings] = useState({
    transferRegistration: true,
    autoReview: false,
    smsNotification: true,
  });
  const [personName, setPersonName] = useState("");
  const [personRole, setPersonRole] = useState("receiver");
  const [editingId, setEditingId] = useState(null);
  const [people, setPeople] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPeople = useCallback(async () => {
    const response = await fetch(apiUrl("/api/transfers/people"), {
      headers: adminAuthHeaders(),
    });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "خطا در دریافت اشخاص حواله");
    }

    setPeople(data.people || []);
  }, []);

  const loadTransfers = useCallback(async () => {
    const response = await fetch(apiUrl("/api/transfers/all?limit=100"), {
      headers: adminAuthHeaders(),
    });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "خطا در دریافت لیست حواله‌ها");
    }

    setTransfers(data.transfers || []);
  }, []);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([loadPeople(), loadTransfers()]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [loadPeople, loadTransfers]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const toggleSetting = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resetPersonForm = () => {
    setPersonName("");
    setPersonRole("receiver");
    setEditingId(null);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "sender":
        return "ارسال حواله";
      case "both":
        return "دریافت و ارسال";
      case "receiver":
      default:
        return "دریافت حواله";
    }
  };

  const getUnitLabel = (unit) => (unit === "gram" ? "گرم" : "ریال");

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return "تایید شده";
      case "rejected":
        return "رد شده";
      case "pending":
      default:
        return "در انتظار بررسی";
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("fa-IR");
  };

  const handlePersonSubmit = async (event) => {
    event.preventDefault();

    const normalizedName = personName.trim();
    if (!normalizedName) {
      toast.error("نام شخص را وارد کنید.");
      return;
    }

    try {
      const response = await fetch(apiUrl(editingId ? `/api/transfers/people/${editingId}` : "/api/transfers/people"), {
        method: editingId ? "PUT" : "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ name: normalizedName, role: personRole }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "خطا در ذخیره شخص");
      }

      toast.success(editingId ? "شخص ویرایش شد." : "شخص اضافه شد.");
      resetPersonForm();
      await loadPeople();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditPerson = (person) => {
    setPersonName(person.name);
    setPersonRole(person.role);
    setEditingId(person.id);
  };

  const handleDeletePerson = async (personId) => {
    try {
      const response = await fetch(apiUrl(`/api/transfers/people/${personId}`), {
        method: "DELETE",
        headers: adminAuthHeaders(),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "خطا در حذف شخص");
      }

      toast.info("شخص حذف شد.");
      if (editingId === personId) {
        resetPersonForm();
      }
      await loadPeople();
      await loadTransfers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleTransferStatus = async (transferId, action) => {
    try {
      const response = await fetch(apiUrl(`/api/transfers/${transferId}/${action}`), {
        method: "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: action === "reject" ? JSON.stringify({ reason: "رد شده توسط مدیر" }) : undefined,
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "خطا در تغییر وضعیت حواله");
      }

      toast.success(action === "approve" ? "حواله تایید شد." : "حواله رد شد.");
      await loadTransfers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div style={{ display: "grid", gap: "24px" }}>
      <section style={cardStyle}>
        <div style={{ marginBottom: "18px" }}>
          <h3 style={{ margin: 0, color: "#ffcc00" }}>تنظیمات</h3>
          <p style={{ margin: "8px 0 0", color: "rgba(255,255,255,0.72)" }}>
            تنظیمات اصلی ثبت و مدیریت حواله‌ها از این بخش کنترل می‌شود.
          </p>
        </div>

        <SettingToggle
          title="فعال بودن ثبت حواله"
          description="دسترسی کاربران به فرم ثبت حواله را روشن یا خاموش می‌کند."
          enabled={settings.transferRegistration}
          onToggle={() => toggleSetting("transferRegistration")}
        />
        <SettingToggle
          title="بررسی خودکار"
          description="در صورت آماده شدن منطق بک‌اند، حواله‌های جدید به‌صورت خودکار بررسی می‌شوند."
          enabled={settings.autoReview}
          onToggle={() => toggleSetting("autoReview")}
        />
        <SettingToggle
          title="اعلان پیامکی"
          description="برای تغییر وضعیت حواله به کاربر اعلان ارسال می‌شود."
          enabled={settings.smsNotification}
          onToggle={() => toggleSetting("smsNotification")}
        />

        <div
          style={{
            marginTop: "24px",
            paddingTop: "20px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <strong style={{ display: "block", marginBottom: "6px", color: "#ffcc00" }}>
              اشخاص دریافت یا ارسال حواله
            </strong>
            <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "14px" }}>
              شخص جدید تعریف کنید و بعداً از همین لیست ویرایش یا حذفش کنید.
            </span>
          </div>

          <form onSubmit={handlePersonSubmit} className="TransferAdminPersonForm">
            <label style={{ display: "grid", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>نام شخص</span>
              <input
                type="text"
                value={personName}
                onChange={(event) => setPersonName(event.target.value)}
                placeholder="مثلاً شرکت پارس"
                style={inputStyle}
              />
            </label>

            <label style={{ display: "grid", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>نوع دسترسی</span>
              <select
                value={personRole}
                onChange={(event) => setPersonRole(event.target.value)}
                style={inputStyle}
              >
                <option value="receiver">دریافت حواله</option>
                <option value="sender">ارسال حواله</option>
                <option value="both">دریافت و ارسال</option>
              </select>
            </label>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                type="submit"
                style={{
                  ...actionButtonStyle,
                  backgroundColor: "#ffcc00",
                  color: "#000",
                }}
              >
                {editingId ? "ذخیره ویرایش" : "افزودن شخص"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetPersonForm}
                  style={{
                    ...actionButtonStyle,
                    backgroundColor: "#2b2b2b",
                    color: "#fff",
                  }}
                >
                  انصراف
                </button>
              )}
            </div>
          </form>

          <div style={{ display: "grid", gap: "10px" }}>
            {loading ? (
              <div style={{ color: "rgba(255,255,255,0.72)" }}>در حال دریافت اطلاعات...</div>
            ) : people.length === 0 ? (
              <div
                style={{
                  border: "1px dashed rgba(255,255,255,0.18)",
                  borderRadius: "12px",
                  padding: "18px",
                  textAlign: "center",
                  color: "rgba(255,255,255,0.72)",
                }}
              >
                هنوز شخصی برای حواله تعریف نشده است.
              </div>
            ) : (
              people.map((person) => (
                <div
                  key={person.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    backgroundColor: "#171717",
                    border: "1px solid rgba(255,255,255,0.08)",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "grid", gap: "6px" }}>
                    <strong>{person.name}</strong>
                    <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "14px" }}>
                      {getRoleLabel(person.role)}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => handleEditPerson(person)}
                      style={{
                        ...actionButtonStyle,
                        backgroundColor: "rgba(255, 204, 0, 0.14)",
                        color: "#ffcc00",
                      }}
                    >
                      ویرایش
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePerson(person.id)}
                      style={{
                        ...actionButtonStyle,
                        backgroundColor: "rgba(220, 53, 69, 0.18)",
                        color: "#ff808d",
                      }}
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            marginBottom: "18px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: "#ffcc00" }}>لیست حواله‌ها</h3>
            <p style={{ margin: "8px 0 0", color: "rgba(255,255,255,0.72)" }}>
              حواله‌های ثبت‌شده در این بخش نمایش داده می‌شوند.
            </p>
          </div>
          <span
            style={{
              backgroundColor: "rgba(255, 204, 0, 0.12)",
              color: "#ffcc00",
              borderRadius: "999px",
              padding: "8px 12px",
              fontWeight: "bold",
            }}
          >
            {formatDisplayNumber(transfers.length, 0)} حواله
          </span>
        </div>

        {loading ? (
          <div style={{ color: "rgba(255,255,255,0.72)" }}>در حال دریافت حواله‌ها...</div>
        ) : transfers.length === 0 ? (
          <div
            style={{
              border: "1px dashed rgba(255,255,255,0.18)",
              borderRadius: "12px",
              padding: "28px 18px",
              textAlign: "center",
              color: "rgba(255,255,255,0.72)",
            }}
          >
            هنوز حواله‌ای برای نمایش وجود ندارد.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#1b1b1b", color: "#ffcc00" }}>
                  <th style={tableHeadStyle}>#</th>
                  <th style={tableHeadStyle}>کاربر</th>
                  <th style={tableHeadStyle}>مقدار</th>
                  <th style={tableHeadStyle}>گیرنده</th>
                  <th style={tableHeadStyle}>نزد</th>
                  <th style={tableHeadStyle}>وضعیت</th>
                  <th style={tableHeadStyle}>تاریخ</th>
                  <th style={tableHeadStyle}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer, index) => (
                  <tr key={transfer.id}>
                    <td style={tableCellStyle}>{index + 1}</td>
                    <td style={tableCellStyle}>{transfer.applicantName || "کاربر"}</td>
                    <td style={tableCellStyle}>
                      {formatDisplayNumber(transfer.amount)} {getUnitLabel(transfer.unit)}
                    </td>
                    <td style={tableCellStyle}>{transfer.receiverName}</td>
                    <td style={tableCellStyle}>{transfer.personName || "-"}</td>
                    <td style={tableCellStyle}>{getStatusLabel(transfer.status)}</td>
                    <td style={tableCellStyle}>{formatDate(transfer.createdAt)}</td>
                    <td style={tableCellStyle}>
                      {transfer.status === "pending" ? (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => handleTransferStatus(transfer.id, "approve")}
                            style={{ ...actionButtonStyle, backgroundColor: "#1f7a39", color: "#fff" }}
                          >
                            تایید
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTransferStatus(transfer.id, "reject")}
                            style={{ ...actionButtonStyle, backgroundColor: "#8b1e2d", color: "#fff" }}
                          >
                            رد
                          </button>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default TransferManagement;
